import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

/**
 * Lazy, singleton Firebase initialization with local-emulator wiring.
 *
 * In emulator mode (VITE_USE_EMULATORS=true) any non-empty config works — the
 * emulators don't validate real credentials — so we fall back to demo values
 * when the VITE_FIREBASE_* vars are blank. In production those vars MUST be
 * set, or init throws loudly (no silent misconfig).
 */
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === 'true';
const EMULATOR_HOST = import.meta.env.VITE_EMU_HOST || 'localhost';
// easy-moving-io uses its own sequential emulator port block. Dev runs on 62xx
// (firebase.json); the e2e suite points these VITE_EMU_* vars at a throwaway
// 63xx test stack (firebase.test.json) so tests never touch dev data.
const EMULATOR_PORTS = {
  auth: Number(import.meta.env.VITE_EMU_AUTH) || 6203,
  firestore: Number(import.meta.env.VITE_EMU_FIRESTORE) || 6204,
  storage: Number(import.meta.env.VITE_EMU_STORAGE) || 6206,
  functions: Number(import.meta.env.VITE_EMU_FUNCTIONS) || 6205,
} as const;

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _functions: Functions | null = null;

function readConfig() {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || (USE_EMULATORS ? 'easy-moving-io' : '');
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY || (USE_EMULATORS ? 'demo-api-key' : '');
  const appId = import.meta.env.VITE_FIREBASE_APP_ID || (USE_EMULATORS ? 'demo-app-id' : '');
  const authDomain =
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || `${projectId || 'demo'}.firebaseapp.com`;
  const storageBucket =
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || `${projectId || 'demo'}.firebasestorage.app`;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '0';

  if (!apiKey || !projectId || !appId) {
    throw new Error(
      'Firebase config missing. Set VITE_FIREBASE_* env vars (see .env.example), ' +
        'or set VITE_USE_EMULATORS=true to run against the local emulators.',
    );
  }
  return { apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId };
}

export function firebaseApp(): FirebaseApp {
  if (_app) return _app;
  _app = initializeApp(readConfig());
  return _app;
}

export function firebaseAuth(): Auth {
  if (_auth) return _auth;
  _auth = getAuth(firebaseApp());
  if (USE_EMULATORS) {
    connectAuthEmulator(_auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
      disableWarnings: true,
    });
  }
  return _auth;
}

export function firebaseDb(): Firestore {
  if (_db) return _db;
  // ignoreUndefinedProperties lets optional item/box fields be omitted without
  // hand-stripping `undefined` before every write.
  _db = initializeFirestore(firebaseApp(), { ignoreUndefinedProperties: true });
  if (USE_EMULATORS) connectFirestoreEmulator(_db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
  return _db;
}

export function firebaseStorage(): FirebaseStorage {
  if (_storage) return _storage;
  _storage = getStorage(firebaseApp());
  if (USE_EMULATORS) connectStorageEmulator(_storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
  return _storage;
}

export function firebaseFunctions(): Functions {
  if (_functions) return _functions;
  // Callables are deployed in europe-west1 (functions/src/index.ts).
  _functions = getFunctions(firebaseApp(), 'europe-west1');
  if (USE_EMULATORS) {
    connectFunctionsEmulator(_functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
  }
  return _functions;
}

export const usingEmulators = USE_EMULATORS;
